import { capitalizeFirstLetter } from '../../../utils/stringUtils'
import {
  deleteMeal,
  insertMeal,
  MEAL_TYPES,
  updateMeal,
  type MealType,
} from '../../../utils/calendarUtils'
import type { ExtraButton } from '../../PopupForm/Popup'
import { getMessage } from '../../../utils/messageUtils'
import CalendarPopupContent from './CalendarPopupContent'
import {
  AlertTime,
  getRecipeExtraData,
  setRecipeMode,
  type recipe,
} from '../../../utils/recipeUtils'

import './Meal.scss'
import { Fragment, useState } from 'react'
import Popup from '../../PopupForm/Popup'
import Spinner from '../../Spinner/Spinner'
import NumericInput from '../../PopupForm/NumericInput'
import { completeRecipe } from '../../../utils/storageUtils'
import PopupError from '../../PopupForm/PopupError'
import type { Mode } from '../../../App'
import type { User } from '../../../utils/loginUtils'

interface MealProps {
  recipeId: number | null
  type: MealType
  date: string
  dateDay: string
  dateId: number
  comesFromTupper: boolean
  alert: string | null
  alertTime: AlertTime | null
  user: User
  existingRecipes: recipe[] | undefined
  openPopup: (
    message: string,
    children: React.ReactNode,
    extraButtons: ExtraButton[]
  ) => void
  closePopup: () => void
  setPopupOnOk: React.Dispatch<React.SetStateAction<() => void>>
  updateWeekInfo: (
    day: number,
    mealType: MealType,
    recipeId: number | null,
    fromTupper: boolean,
    alert: string | null,
    alertTime: AlertTime | null
  ) => void
  setMode: (mode: Mode) => void
  isMobile: boolean
}

function Meal({
  recipeId,
  type,
  date,
  dateDay,
  dateId,
  comesFromTupper,
  alert,
  alertTime,
  user,
  existingRecipes,
  openPopup,
  closePopup,
  setPopupOnOk,
  updateWeekInfo,
  setMode,
  isMobile,
}: MealProps) {
  const [extraPopupOpen, setExtraPopupOpen] = useState(false)
  const [extraPopupErrorMessage, setExtraPopupErrorMessage] = useState('')
  const [tuppersToAdd, setTuppersToAdd] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const checkDifferentDay = (
    newRecipeId: number,
    newAlert: string | null,
    newAlertTime: AlertTime | null
  ) => {
    return (
      recipeId !== newRecipeId ||
      alert !== newAlert ||
      alertTime !== newAlertTime
    )
  }

  const storeRecipe = async (
    newRecipeId: number,
    fromTupper: boolean,
    newAlert: string | null,
    newAlertTime: AlertTime | null
  ) => {
    setIsLoading(true)
    if (recipeId === null && newRecipeId === null) return
    // Insert and update need to go separately for shopping management
    if (recipeId === null && newRecipeId !== null) {
      await insertMeal(
        date,
        MEAL_TYPES[type as keyof typeof MEAL_TYPES],
        newRecipeId,
        newAlert,
        newAlertTime,
        user.id!
      )
      updateWeekInfo(
        dateId,
        type,
        newRecipeId,
        fromTupper,
        newAlert,
        newAlertTime
      )
    } else if (
      recipeId !== null &&
      checkDifferentDay(newRecipeId, newAlert, newAlertTime)
    ) {
      await updateMeal(
        date,
        MEAL_TYPES[type as keyof typeof MEAL_TYPES],
        newRecipeId,
        newAlert,
        newAlertTime,
        user.id!
      )
      updateWeekInfo(
        dateId,
        type,
        newRecipeId,
        fromTupper,
        newAlert,
        newAlertTime
      )
    }
    setIsLoading(false)
  }

  const handleExtraButton = async (newRecipeId: number) => {
    await storeRecipe(newRecipeId, false, null, null)
    closePopup()
  }

  const clickRecipeMode = () => {
    const recipe = existingRecipes?.find(r => r.id === recipeId) || null
    setRecipeMode(user.id!, recipe, null, null, setMode)
  }

  const handleDelete = async () => {
    setIsLoading(true)
    await deleteMeal(
      date,
      MEAL_TYPES[type as keyof typeof MEAL_TYPES],
      user.id!
    )
    updateWeekInfo(dateId, type, null, false, null, null)
    setIsLoading(false)
    closePopup()
  }

  const handleClick = () => {
    const extraButtons = [
      {
        label: getMessage('question_mark'),
        className: 'calendar-popup-question-button',
        onClick: () => handleExtraButton(-1),
      },
      {
        label: getMessage('x_mark'),
        className: 'calendar-popup-x-button',
        onClick: () => handleExtraButton(0),
      },
    ] as ExtraButton[]

    if (recipeId !== null && recipeId > 0) {
      extraButtons.push({
        label: getMessage('recipe_cook'),
        className: 'calendar-popup-cook-button',
        onClick: () => clickRecipeMode(),
      })
      extraButtons.push({
        label: getMessage('complete'),
        className: 'calendar-popup-complete-button',
        onClick: () => openExtraPopup(),
      })
      extraButtons.push({
        label: getMessage('delete'),
        className: 'calendar-popup-delete-button',
        onClick: handleDelete,
      })
    }

    openPopup(
      `${capitalizeFirstLetter(type)} ${getMessage('for')} ${dateDay}`,
      <CalendarPopupContent
        recipeId={recipeId}
        type={type}
        recipeFromTupper={comesFromTupper}
        recipeAlert={alert}
        recipeAlertTime={alertTime}
        existingRecipes={existingRecipes}
        user={user}
        setPopupOnOk={setPopupOnOk}
        storeRecipe={storeRecipe}
        closePopup={closePopup}
        isMobile={isMobile}
      />,
      extraButtons
    )
  }

  const openExtraPopup = async () => {
    setIsLoading(true)
    const data = await getRecipeExtraData(user.id!, recipeId!)
    setTuppersToAdd(data.tuppers || 0)
    closePopup()
    setExtraPopupOpen(true)
    setIsLoading(false)
  }

  const completeExistingRecipe = async () => {
    setIsLoading(true)
    const result = (await completeRecipe(
      user.id!,
      recipeId!,
      tuppersToAdd
    )) as {
      success: boolean
      error?: string
    }
    setIsLoading(false)
    if (!result.success) {
      setExtraPopupErrorMessage(
        getMessage('recipe_save_error') + (result.error || '')
      )
    }
    setExtraPopupOpen(false)
    closePopup()
  }

  const extraPopupContent = (
    <div className={`pop-up-form ${isMobile ? 'pop-up-form-mobile' : ''}`}>
      <PopupError
        errorMessage={extraPopupErrorMessage}
        setErrorMessage={setExtraPopupErrorMessage}
      />
      <NumericInput
        className={`pop-up-form-label ${isMobile ? 'pop-up-form-label-mobile' : ''}`}
        label={getMessage('tuppers_generated')}
        value={tuppersToAdd}
        setValue={setTuppersToAdd}
        isMobile={isMobile}
      />
    </div>
  )

  const classNameSpecial =
    recipeId !== null && recipeId === 0
      ? ' calendar-box-empty'
      : recipeId !== null && recipeId === -1
        ? 'calendar-box-question'
        : ''

  const classNameTupper = comesFromTupper ? ' calendar-box-tupper' : ''

  const recipe = existingRecipes?.find(r => r.id === recipeId) || null
  const recipeName =
    !recipe || (recipeId !== null && (recipeId === 0 || recipeId === -1))
      ? ''
      : recipe?.name

  return (
    <Fragment>
      <div
        className={`calendar-box calendar-box-${type} ${isMobile ? 'calendar-box-mobile' : ''}  ${classNameSpecial} ${classNameTupper}`}
        onClick={handleClick}
      >
        {recipeName}
      </div>
      <Popup
        isOpen={extraPopupOpen}
        message={getMessage('recipe_complete_confirm', [
          tuppersToAdd.toString(),
        ])}
        isMobile={isMobile}
        children={extraPopupContent}
        onOk={() => completeExistingRecipe()}
        onClose={() => setExtraPopupOpen(false)}
      />
      <Spinner
        isOpen={isLoading}
        message={getMessage('loading')}
        overlay={true}
      />
    </Fragment>
  )
}

export default Meal
